/**
 * 播放介面的共用視覺元件。只負責呈現與操作事件，不持有播放流程狀態。
 */
import { useEffect, useState } from "react";
import { formatTime } from "../playbackNavigation.js";

const ICON_PATHS = {
  music: <><path d="M9 18V5l10-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="16" cy="16" r="3"/></>,
  search: <><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></>,
  list: <><path d="M9 6h11M9 12h11M9 18h11"/><path d="M4 6h.01M4 12h.01M4 18h.01"/></>,
  play: <path d="m8 5 11 7-11 7V5Z"/>,
  pause: <><path d="M9 5v14"/><path d="M15 5v14"/></>,
  previous: <><path d="M6 5v14"/><path d="m18 6-9 6 9 6V6Z"/></>,
  next: <><path d="M18 5v14"/><path d="m6 6 9 6-9 6V6Z"/></>,
  repeat: <><path d="m17 2 4 4-4 4"/><path d="M3 11V9a3 3 0 0 1 3-3h15"/><path d="m7 22-4-4 4-4"/><path d="M21 13v2a3 3 0 0 1-3 3H3"/></>,
  repeatOne: <><path d="m17 2 4 4-4 4"/><path d="M3 11V9a3 3 0 0 1 3-3h15"/><path d="m7 22-4-4 4-4"/><path d="M21 13v2a3 3 0 0 1-3 3H3"/><path d="M12 10v4"/><path d="m10.8 11.2 1.2-1.2"/></>,
  shuffle: <><path d="M16 3h5v5"/><path d="m4 20 5.5-5.5"/><path d="m15 9 6-6"/><path d="M4 4l12 12"/><path d="M16 16h5v5"/></>,
  heart: <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z"/>,
  plus: <><path d="M12 5v14"/><path d="M5 12h14"/></>,
  lyrics: <><path d="M5 4h14v13H8l-3 3V4Z"/><path d="M9 9h6M9 13h4"/></>,
  close: <><path d="m6 6 12 12"/><path d="m18 6-12 12"/></>,
  chevronDown: <path d="m6 9 6 6 6-6"/>,
  settings: <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1A1.7 1.7 0 0 0 9 4.6 1.7 1.7 0 0 0 10 3v-.2h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z"/></>,
  refresh: <><path d="M20 11a8 8 0 1 0-2.3 5.7"/><path d="M20 4v7h-7"/></>,
  download: <><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></>,
  more: <><circle cx="5" cy="12" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/></>,
};

export function Icon({ name, size = 22, filled = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ICON_PATHS[name] || ICON_PATHS.music}
    </svg>
  );
}

export function Artwork({ song, className = "" }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [song?.thumbnailUrl]);
  return (
    <div className={`artwork ${className}`}>
      {song?.thumbnailUrl && !failed
        ? <img src={song.thumbnailUrl} alt="" onError={() => setFailed(true)} />
        : <div className="artwork-fallback"><Icon name="music" size={42}/></div>}
    </div>
  );
}

export function SongRow({ song, active, playing, loading, favorite, onPlay, onAdd, onRemove }) {
  const title = song.title || "未命名歌曲";
  return (
    <div className={`song-row ${active ? "is-active" : ""}`}>
      <button className="song-main" type="button" onClick={onPlay} disabled={loading}
        aria-label={`${active && playing ? "暫停" : "播放"}：${title}`} aria-current={active ? "true" : undefined}>
        <Artwork song={song} className="row-art" />
        <span className="song-copy">
          <strong>{title}</strong>
          <small>{(song.artists || []).join("、") || "未知演出者"}</small>
        </span>
        {favorite && <span className="song-saved" aria-label="已收藏"><Icon name="heart" size={14} filled/></span>}
        <span className="song-duration">{song.durationMs > 0 ? formatTime(song.durationMs / 1000) : ""}</span>
        <span className="row-play">{loading ? <span className="spinner"/> : <Icon name={active && playing ? "pause" : "play"} size={18}/>}</span>
      </button>
      {onAdd && <button className="row-action" type="button" onClick={onAdd}
        aria-label={`選擇加入位置：${title}`} aria-haspopup="dialog"><Icon name="plus" size={20}/></button>}
      {onRemove && <button className="row-action row-remove" type="button" onClick={onRemove}
        aria-label={`從目前列表移除：${title}`}><Icon name="close" size={18}/></button>}
    </div>
  );
}
